import { useEffect, useMemo, useState, useContext } from 'react'
import { TableContext } from 'Context/Table'
import ActionCell from 'Presentation/Components/Table/Components/CustomActionCell'
import { Link, useNavigate } from 'react-router-dom'
import { useMachineStore } from 'Store/Machine'
import moment from 'moment'
import { toast } from 'react-toastify'
import { shallow } from 'zustand/shallow'
import Swal from 'sweetalert2'

const INITIAL_FILTER = {
  q: '',
  machineTypeID: null,
}

const ViewModel = () => {
  const navigate = useNavigate()
  const { rawData, types, getAll, getTypes, deleteMachine, clearState } = useMachineStore(
    state => ({
      rawData: state.data,
      types: state.types,
      getAll: state.getAll,
      getTypes: state.getTypes,
      deleteMachine: state.delete,
      clearState: state.clearState,
    }),
    shallow
  )
  const {
    updateLoading,
    updateError,
    updatePagination,
    updateSorting,
    resetState: resetTableState,
  } = useContext(TableContext)

  const [filter, setFilter] = useState(INITIAL_FILTER)

  const data = useMemo(() => {
    return rawData
  }, [rawData])
  const typeOptions = types.map((type: any) => ({
    label: type.title,
    value: type.id,
  }))

  const onChangeFilter = (name: string, value: any) => {
    setFilter(prev => ({ ...prev, [name]: value }))
  }

  const fetchData = () => {
    getTypes()

    updateLoading(true)
    updateError(false)

    getAll(filter).then(({ data, success }) => {
      if (!success) {
        updateLoading(false)
        updateError(true)
        toast.error('เกิดข้อผิดพลาดในการดึงข้อมูล : ' + data)
      } else {
        updateLoading(false)
        updateError(false)
      }
    })
  }

  const setupTable = () => {
    updateSorting('id', true)
    updatePagination(true)
  }

  const onAdd = () => {
    navigate('/machine/create')
  }

  const onView = (id: any) => {
    navigate(`/machine/${id}`)
  }

  const onDelete = (id: any) => {
    Swal.fire({
      title: 'ลบข้อมูลเครื่องจักร',
      text: 'คุณต้องการที่จะลบเครื่องจักรนี้หรือไม่',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    }).then(async result => {
      if (result.isConfirmed) {
        const { data, success } = await deleteMachine(id)
        if (!success) {
          toast.error('เกิดข้อผิดพลาดในการลบข้อมูล : ' + data)
        } else {
          fetchData()
          toast.success('ลบข้อมูลสำเร็จ')
        }
      }
    })
  }

  const onClearFilter = () => {
    setFilter(INITIAL_FILTER)
    getAll(INITIAL_FILTER)
  }

  const onSubmitFilter = () => {
    fetchData()
  }

  const TABLE_CONFIGS: any[] = [
    {
      Header: 'รหัสเครื่องจักร',
      accessor: 'machineCode',
      width: 200,
      minWidth: 200,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'ยี่ห้อเครื่องจักร',
      accessor: 'brand',
      width: 200,
      minWidth: 200,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'รุ่นเครื่องจักร',
      accessor: 'machineModel',
      width: 200,
      minWidth: 200,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'ระบบการตอก',
      accessor: 'pillingType',
      width: 200,
      minWidth: 200,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'ประเภทของเครื่องจักร',
      accessor: 'machineType',
      width: 200,
      minWidth: 200,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value?.title}</span>
      },
    },
    {
      Header: 'โครงการที่ใช้งานอยู่',
      accessor: 'projectMachine',
      width: 200,
      minWidth: 200,
      Cell: ({ value }: any) => {
        return (
          value && (
            <span className='text-gray-800'>
              <Link to={`/project/${value?.projectId}/machine`}>{value?.division ?? '-'}</Link>
            </span>
          )
        )
      },
    },
    {
      Header: 'วันที่สร้าง',
      accessor: 'createdAt',
      width: 100,
      minWidth: 100,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {value ? moment(value).tz('Asia/Bangkok').format('DD/MM/YYYY HH:mm') : '-'}
          </span>
        )
      },
    },
    {
      Header: '',
      accessor: 'action',
      width: 100,
      minWidth: 100,
      Cell: ({ row }: any) => (
        <div className='d-flex justify-content-end'>
          <ActionCell
            id={row.original.id}
            hideEdit
            hideDuplicate
            onClickView={() => onView(row.original.id)}
            onClickDelete={() => onDelete(row.original.id)}
          />
        </div>
      ),
      disableSortBy: true,
    },
  ]

  useEffect(() => {
    setupTable()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      clearState()
      resetTableState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    data,
    TABLE_CONFIGS,
    typeOptions,
    filter,
    onAdd,
    onChangeFilter,
    onSubmitFilter,
    onClearFilter,
  }
}

export default ViewModel
